import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { refreshCustomerAccessToken } from '@/lib/customer-token-refresh';

/**
 * Customer Module Phase 3 — dedicated Axios instance for the Customer
 * Portal (D-050). Its own interceptors, its own token source
 * (`useCustomerAuthStore`) — never shares an interceptor or token-storage
 * key with `apiClient` (tenant staff) or `platformApiClient` (Platform
 * Owner), so a customer token can never be sent on a staff/platform request
 * or vice versa.
 */
export const customerApiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
  withCredentials: true,
});

// ── Request: attach current access token ────────────────────────────────────
customerApiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!config.headers.has('Authorization')) {
    const token = useCustomerAuthStore.getState().accessToken;
    if (token !== null) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
  }
  return config;
});

// ── Response: unwrap { data, meta } envelope ─────────────────────────────────
customerApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const body = response.data as Record<string, unknown> | null | undefined;
    if (body !== null && body !== undefined && typeof body === 'object' && 'data' in body) {
      return { ...response, data: body['data'] };
    }
    return response;
  },
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }

    const config = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    // The login endpoint's own 401 means "invalid credentials," not "session
    // expired" — there is no session yet to refresh.
    if (config?.url?.includes('/customer-portal/auth/login')) {
      return Promise.reject(error);
    }

    // Guard: don't retry if already retried, or if this IS the refresh endpoint
    if (!config || config._retry || config.url?.includes('/customer-portal/auth/refresh')) {
      useCustomerAuthStore.getState().clearAuth();
      return Promise.reject(new Error('Session expired. Please sign in again.'));
    }

    // Optimistic check: another mechanism already refreshed — retry with the new token.
    const currentToken = useCustomerAuthStore.getState().accessToken;
    const sentToken = (config.headers.get('Authorization') as string | undefined)?.replace(
      'Bearer ',
      '',
    );

    if (currentToken && sentToken && currentToken !== sentToken) {
      config._retry = true;
      config.headers.set('Authorization', `Bearer ${currentToken}`);
      return customerApiClient(config);
    }

    config._retry = true;
    try {
      const newToken = await refreshCustomerAccessToken();
      config.headers.set('Authorization', `Bearer ${newToken}`);
      return customerApiClient(config);
    } catch {
      return Promise.reject(new Error('Session expired. Please sign in again.'));
    }
  },
);
